import { GcfPriceLabelTypeEnum } from 'src/interfaces';

import {
  GcfCommonComponentConfigInterface,
  SubHeaderConfigSchema,
} from '../FormSelector.interface';

type Omit<T, K extends keyof GcfCommonComponentConfigInterface> = Pick<
  T,
  Exclude<keyof T, K>
>;

const exclude_list = ['title', 'disclaimer', 'autoScale', 'header'] as const;

type Exclude_type = (typeof exclude_list)[number];

interface PromotionalSpaceV3BaseInterface
  extends Omit<GcfCommonComponentConfigInterface, Exclude_type> {
  promotionUid?: string;
  promotionName?: string;
  preview?: boolean;
}

export interface PromotionalSpaceV3Interface
  extends PromotionalSpaceV3BaseInterface {
  ads?: {
    showPlaceholder?: boolean;
    isAdsEnabled?: boolean;
  };
  backups?: PromotionalSpaceV3BaseInterface[];
}

const PromotionalSpaceV3BaseSchema = {
  properties: {
    typeface: {
      type: 'object',
      properties: {
        regular: {
          type: 'string',
        },
        bold: {
          type: 'string',
        },
      },
      required: ['regular', 'bold'],
    },
    subheader: {
      type: 'array',
      items: [SubHeaderConfigSchema],
    },
    productStyle: {
      type: 'object',
      properties: {
        color: {
          type: 'string',
        },
      },
      required: [],
    },
    priceLabel: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: [
            GcfPriceLabelTypeEnum.Size,
            GcfPriceLabelTypeEnum.Acronym,
            GcfPriceLabelTypeEnum.None,
          ],
        },
        shape: {
          type: 'string',
          enum: ['NONE', 'PILL'],
        },
      },
      required: ['type', 'shape'],
    },
    subProductsDisplayFormat: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: ['INLINE', 'MULTILINE'],
        },
      },
      required: ['type'],
    },
    background: {
      type: 'string',
      minLength: 1,
    },
    datasource: {
      oneOf: [
        {
          type: 'string',
        },
      ],
    },

    highlight: {
      type: 'object',
      properties: {
        color: { type: 'string' },
      },
    },
    cycling: {
      type: 'object',
      properties: {
        offset: {
          type: 'number',
        },
        order: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              mappingKeys: {
                type: 'array',
                items: {
                  type: 'string',
                },
              },
              duration: {
                type: 'number',
              },
            },
            required: ['mappingKeys', 'duration'],
          },
        },
      },
      required: ['order'],
    },
    scaleTypeKey: {
      type: 'string',
    },
    flavor: {
      type: 'object',
      properties: {
        visible: {
          type: 'boolean',
          default: false,
        },
      },
      required: ['visible'],
    },
    show_nutrition: {
      type: 'boolean',
    },
    promotionUid: {
      type: 'string',
    },
    promotionName: {
      type: 'string',
    },
    showComponentProductDisclaimer: {
      type: 'boolean',
      default: false,
    },
    isFlavourCycling: {
      type: 'boolean',
      default: false,
    },
    ads: {
      type: 'object',
      properties: {
        showPlaceholder: {
          type: 'boolean',
          default: false,
        },
        isAdsEnabled: {
          type: 'boolean',
          default: false,
        },
      },
      required: [],
    },
  },
  required: ['background'],
  additionalProperties: true,
};

const PromotionalSpaceV3BackupSchema = {
  backups: {
    type: 'array',
    items: {
      type: 'object',
      properties: {
        ...PromotionalSpaceV3BaseSchema.properties,
        preview: {
          type: 'boolean',
        },
      },
      required: PromotionalSpaceV3BaseSchema.required,
      additionalProperties: PromotionalSpaceV3BaseSchema.additionalProperties,
    },
  },
};

export const PromotionalSpaceV3Schema = {
  title: 'Promotional Space V3 JSON Config Schema',
  description: 'JSON Configuration Options for Promotional Space V3 Component',
  type: 'object',
  properties: {
    ...PromotionalSpaceV3BaseSchema.properties,
    backups: PromotionalSpaceV3BackupSchema.backups,
  },
  required: PromotionalSpaceV3BaseSchema.required,
};
